import { Link } from 'react-router-dom';
import { AttentionItem } from './AttentionItem.jsx';
import { ProgressBar } from './ProgressBar.jsx';
import { StatusBadge } from './StatusBadge.jsx';
import { formatDate, formatDateTime } from '../utils/formatDate.js';
import { projectHealth } from '../utils/statusUi.js';
import { isActiveTask, stageCaption, stageHasWaiting, summarizeTaskProgress } from '../utils/taskProgress.js';
import { initials } from '../utils/userDisplay.js';

export function ProjectOverviewBoard({
  project,
  tasks = [],
  activity = [],
  meetings = [],
  notes,
  clientName,
  siteUrl,
  dueDate,
  onOpenTab,
  variant = 'admin',
  summary: summaryProp = null,
}) {
  const summary = summaryProp || summarizeTaskProgress(tasks, project?.task_columns, { hideInternal: variant === 'client' });
  const stages = summary.stages;
  const progress = summary.percent;
  const health = projectHealth(project);
  const openTasks = tasks.filter((task) => isActiveTask(task, project?.task_columns));
  const overdueTasks = openTasks.filter((t) => t.due_date && String(t.due_date).slice(0, 10) < new Date().toISOString().slice(0, 10));
  const clientWaiting = openTasks.filter((t) => t.is_client_task || t.kind === 'client_request' || (stageHasWaiting(project?.task_columns, t.status) && t.waiting_on === 'client'));
  const current = stages.find((s) => s.state === 'current');
  const next = stages.find((s) => s.state === 'upcoming');
  const lead = openTasks.find((t) => t.assignee?.name)?.assignee?.name;

  const attention = [];
  if (overdueTasks.length) {
    attention.push({
      tone: 'danger',
      title: `${overdueTasks.length} overdue task${overdueTasks.length === 1 ? '' : 's'}`,
      detail: 'These items are past their due date',
      action: 'View',
      onClick: () => onOpenTab?.('tasks'),
    });
  }
  if (clientWaiting.length) {
    attention.push({
      tone: 'warning',
      title: `${clientWaiting.length} item${clientWaiting.length === 1 ? '' : 's'} waiting on client`,
      detail: 'Waiting on files, feedback, or approvals',
      action: 'View',
      onClick: () => onOpenTab?.('tasks'),
    });
  }
  if (health.tone === 'info') {
    attention.push({
      tone: 'info',
      title: 'Project is ready for review',
      detail: health.detail,
      action: 'Review',
      onClick: () => onOpenTab?.(variant === 'admin' ? 'settings' : 'notes'),
    });
  }

  const connected = [
    { key: 'ghl', label: 'GoHighLevel', ok: Boolean(project?.ghl_opportunity_id) },
    { key: 'teams', label: 'Microsoft Teams', ok: Boolean(project?.teams_files_web_url || project?.teams_drive_item_id) },
    { key: 'bitwarden', label: 'Bitwarden', ok: Boolean(project?.bitwarden_collection_id) },
  ];

  return (
    <div style={{ marginTop: '1rem' }}>
      <div className="dash-grid">
        <section className="panel">
          <h3 className="panel-title">Project Progress</h3>
          <ProgressBar value={progress} tone="purple" label={`${progress}% complete`} />
          <ol className="timeline-stages" style={{ marginTop: '1rem' }}>
            {stages.map((stage) => (
              <li key={stage.key || stage.name}>
                <span className={`timeline-dot timeline-dot--${stage.state}`} />
                <strong>{stage.name}</strong>
                <div className="muted small">{stageCaption(stage)}</div>
              </li>
            ))}
          </ol>
          <div className="project-hero-meta">
            <div>
              <span className="eyebrow">Current phase</span>
              <strong>{summary.current_stage || current?.name || health.detail}</strong>
            </div>
            <div>
              <span className="eyebrow">Next milestone</span>
              <strong>{summary.next_stage || next?.name || '—'}</strong>
            </div>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Needs Attention</h3>
            {onOpenTab ? (
              <button type="button" className="panel-link" onClick={() => onOpenTab('tasks')}>
                View all items →
              </button>
            ) : null}
          </div>
          {attention.length ? (
            <div className="attention-list">
              {attention.map((item) => (
                <AttentionItem key={item.title} {...item} />
              ))}
            </div>
          ) : (
            <p className="empty-state">Nothing needs attention on this project.</p>
          )}
        </section>
      </div>

      <div className="dash-grid-3">
        <section className="panel">
          <h3 className="panel-title">Tasks</h3>
          <p className="kpi-card-value">{openTasks.length} open</p>
          {overdueTasks.length ? (
            <p className="muted small" style={{ color: 'var(--danger)' }}>
              {overdueTasks.length} overdue
            </p>
          ) : (
            <p className="muted small">No overdue tasks</p>
          )}
        </section>
        <section className="panel">
          <h3 className="panel-title">Client requests</h3>
          <p className="kpi-card-value">{clientWaiting.length} waiting</p>
          <p className="muted small">Files, feedback, or approvals</p>
        </section>
        <section className="panel">
          <h3 className="panel-title">Upcoming</h3>
          {meetings.length ? (
            <ul className="feed-list">
              {meetings.slice(0, 3).map((m) => (
                <li key={m.id}>
                  <span>{m.subject}</span>
                  <span className="muted small">{m.start_at ? formatDateTime(m.start_at) : ''}</span>
                </li>
              ))}
            </ul>
          ) : dueDate ? (
            <p className="muted small">Due {formatDate(dueDate)}</p>
          ) : (
            <p className="empty-state">No upcoming dates.</p>
          )}
        </section>
      </div>

      <div className="dash-grid-3" style={{ marginTop: '1rem' }}>
        <section className="panel">
          <h3 className="panel-title">Project details</h3>
          <div className="usage-stat">
            <span>Website</span>
            <strong>
              {siteUrl ? (
                <a href={siteUrl} target="_blank" rel="noreferrer">
                  Open
                </a>
              ) : (
                '—'
              )}
            </strong>
          </div>
          <div className="usage-stat">
            <span>Start date</span>
            <strong>{project?.created_at ? formatDate(project.created_at) : '—'}</strong>
          </div>
          <div className="usage-stat">
            <span>Due date</span>
            <strong>{dueDate ? formatDate(dueDate) : 'TBD'}</strong>
          </div>
          <div className="usage-stat">
            <span>Client</span>
            <strong>
              {variant === 'admin' && project?.client_id ? (
                <Link to={`/admin/clients/${project.client_id}`}>{clientName || 'Open'}</Link>
              ) : (
                clientName || '—'
              )}
            </strong>
          </div>
          <div className="usage-stat">
            <span>Project lead</span>
            <strong>
              {lead ? (
                <span className="lead-chip">
                  <span className="lead-avatar">{initials(lead)}</span>
                  {lead}
                </span>
              ) : (
                '—'
              )}
            </strong>
          </div>
        </section>

        <section className="panel">
          <h3 className="panel-title">Connected apps</h3>
          <div className="connected-apps">
            {connected.map((app) => (
              <div key={app.key} className="connected-app">
                <span>{app.label}</span>
                <StatusBadge tone={app.ok ? 'success' : 'neutral'} label={app.ok ? 'Connected' : 'Not connected'} dot={false} />
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h3 className="panel-title">Notes</h3>
            {onOpenTab ? (
              <button type="button" className="panel-link" onClick={() => onOpenTab(variant === 'admin' ? 'communication' : 'notes')}>
                {variant === 'admin' ? 'Edit →' : 'View →'}
              </button>
            ) : null}
          </div>
          {notes ? (
            <p className="muted small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
              {notes}
            </p>
          ) : (
            <p className="empty-state">No notes yet.</p>
          )}
          {activity.length ? (
            <ul className="feed-list" style={{ marginTop: '0.75rem' }}>
              {activity.slice(0, 3).map((ev) => (
                <li key={ev.id}>
                  <span>{ev.title}</span>
                  <span className="muted small">{ev.created_at ? formatDateTime(ev.created_at) : ''}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </section>
      </div>
    </div>
  );
}
